import { useTranslation } from '@client-app/i18n';
import { Checkbox } from '@common/index';
import { Briefcase, CircleNotch } from '@phosphor-icons/react';
import { useState } from 'react';
import ApplicationAccessPermissionControl from '../ApplicationAccessPermissionControl';
import Modal from '../Modal';
import { getBusinessGroupDisplayName } from '../brandGroupViewModel';

export type BusinessGroupAssignmentOption = {
    id: string;
    name: string;
    logoUrl?: string | null;
    accent?: string;
    isDefault?: boolean;
    creatorName?: string;
};

type BusinessGroupAccessLevel = 'READ' | 'WRITE';
type BusinessGroupAccessCeiling = BusinessGroupAccessLevel | 'DISABLED';

interface BusinessGroupAssignmentModalProps {
    isOpen: boolean;
    account: string;
    brands: BusinessGroupAssignmentOption[];
    selectedBrandIds: string[];
    assignedBrandIds?: string[];
    accessLevels: Record<string, BusinessGroupAccessLevel>;
    onClose: () => void;
    onToggle: (brandId: string) => void;
    onAccessLevelChange: (brandId: string, level: BusinessGroupAccessLevel) => void;
    onSave: () => void;
    isSaving?: boolean;
    isSaveDisabled?: boolean;
    /**
     * A Project owner may attach another user's application, but cannot set
     * its positive delegation ceiling. Keep the association flow available
     * while rendering the pending DISABLED state instead of offering a
     * permission the backend must reject.
     */
    canSetPositiveAccess?: boolean;
    currentAccessLevels?: Readonly<Record<string, BusinessGroupAccessCeiling>>;
    /** Default Business groups are retained by the connection service. */
    requiredBrandIds?: readonly string[];
    secondaryActionLabel?: string;
    description?: string;
}

export interface BusinessGroupSelectionListProps extends Pick<
    BusinessGroupAssignmentModalProps,
    'brands' | 'selectedBrandIds' | 'assignedBrandIds' | 'accessLevels' | 'onToggle' | 'onAccessLevelChange'
> {
    disabled?: boolean;
    canSetPositiveAccess?: boolean;
    currentAccessLevels?: Readonly<Record<string, BusinessGroupAccessCeiling>>;
    requiredBrandIds?: readonly string[];
}

const BusinessGroupAvatar = ({ brand }: { brand: BusinessGroupAssignmentOption }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const showLogo = Boolean(brand.logoUrl) && !imageFailed;

    return (
        <span
            aria-hidden="true"
            className={`flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md ${
                showLogo
                    ? 'text-white'
                    : 'border border-[#E2E6F2] bg-[#F4F5F8] text-[#1A162F] shadow-xs'
            }`}
            style={showLogo ? { backgroundColor: brand.accent } : undefined}
        >
            {showLogo ? (
                <img
                    src={brand.logoUrl || undefined}
                    alt=""
                    className="h-full w-full bg-white object-contain p-0.5"
                    onError={() => setImageFailed(true)}
                />
            ) : (
                <Briefcase size={16} weight="bold" />
            )}
        </span>
    );
};

export const BusinessGroupSelectionList = ({
    brands,
    selectedBrandIds,
    assignedBrandIds = [],
    accessLevels,
    onToggle,
    onAccessLevelChange,
    disabled = false,
    canSetPositiveAccess = true,
    currentAccessLevels = {},
    requiredBrandIds = [],
}: BusinessGroupSelectionListProps) => {
    const { t } = useTranslation();
    const selectedBrandCount = brands.filter((brand) => selectedBrandIds.includes(brand.id)).length;
    const areAllBrandsSelected = brands.length > 0 && selectedBrandCount === brands.length;
    const areSomeBrandsSelected = selectedBrandCount > 0 && !areAllBrandsSelected;

    const handleToggleAllBrands = (checked: boolean | 'indeterminate') => {
        const shouldSelectAll = checked === true;
        brands.forEach((brand) => {
            if (requiredBrandIds.includes(brand.id)) return;
            if (selectedBrandIds.includes(brand.id) !== shouldSelectAll) {
                onToggle(brand.id);
            }
        });
    };

    return (
        <div className="overflow-hidden rounded-xl border border-[#E8EBF3] bg-white">
            <div role="table" aria-label={t('website.integrationsSettings.businessGroups')} className="w-full">
                <div role="rowgroup" className="bg-[#F8F9FC]">
                    <div role="row" className="grid grid-cols-[minmax(0,1fr)_260px] border-b border-[#E8EBF3]">
                        <div role="columnheader" className="px-4 py-2.5 text-left text-xs font-medium text-[#666F8D]">
                            <span className="flex items-center gap-3">
                                <Checkbox
                                    checked={areSomeBrandsSelected ? 'indeterminate' : areAllBrandsSelected}
                                    disabled={disabled || brands.length === 0}
                                    onCheckedChange={handleToggleAllBrands}
                                    aria-label={t('website.integrationsSettings.selectAllBusinessGroups')}
                                />
                                <span>{t('website.integrationsSettings.businessGroups')}</span>
                            </span>
                        </div>
                        <div role="columnheader" className="px-3 py-2.5 text-left text-xs font-medium text-[#666F8D]">
                            {t('website.integrationsSettings.businessGroupAccessCeiling')}
                        </div>
                    </div>
                </div>
                <div role="rowgroup" className="integrations-apps-scroll-area max-h-[min(480px,calc(55vh-40px))] w-full divide-y divide-[#E8EBF3] overflow-y-auto [overflow-anchor:none]">
                    {brands.map((brand) => {
                        const isSelected = selectedBrandIds.includes(brand.id);
                        const isRequired = requiredBrandIds.includes(brand.id);
                        const isAssigned = assignedBrandIds.includes(brand.id);
                        const currentAccessLevel = currentAccessLevels?.[brand.id];
                        const accessLevel = !canSetPositiveAccess
                            ? currentAccessLevel === 'WRITE' ? 'action' : currentAccessLevel === 'READ' ? 'read' : 'no_access'
                            : accessLevels[brand.id] === 'WRITE' ? 'action' : 'read';

                        return (
                            <div
                                key={brand.id}
                                role="row"
                                onClick={(event) => {
                                    if (disabled || isRequired) return;
                                    if ((event.target as HTMLElement).closest('button')) return;
                                    onToggle(brand.id);
                                }}
                                className={`grid w-full grid-cols-[minmax(0,1fr)_260px] bg-white transition-colors ${brand.creatorName ? 'min-h-[72px]' : 'h-[64px]'} ${disabled ? 'cursor-not-allowed opacity-60' : isRequired ? 'cursor-default' : 'cursor-pointer hover:bg-[#FCFCFD]'}`}
                            >
                                <div role="cell" className="flex min-w-0 items-center px-4">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <Checkbox
                                            checked={isSelected}
                                            disabled={disabled || isRequired}
                                            onCheckedChange={() => onToggle(brand.id)}
                                            aria-label={`${t('website.integrationsSettings.assignmentSelection')} ${getBusinessGroupDisplayName(brand)}`}
                                        />
                                        <button
                                            type="button"
                                            disabled={disabled || isRequired}
                                            onClick={() => onToggle(brand.id)}
                                            aria-pressed={isSelected}
                                            className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8D0E2] focus-visible:ring-offset-2"
                                        >
                                            <BusinessGroupAvatar brand={brand} />
                                            <span className="flex min-w-0 flex-col items-start gap-0.5">
                                                <span className="flex min-w-0 items-center gap-2">
                                                    <span className="min-w-0 truncate text-sm font-medium text-[#1A162F]">
                                                        {getBusinessGroupDisplayName(brand)}
                                                    </span>
                                                    {brand.isDefault ? (
                                                        <span className="shrink-0 rounded-full bg-[#EEF2FF] px-2 py-0.5 text-[11px] font-medium text-[#465A8C]">
                                                            {t('website.brandsSettings.simple.defaultBrandBadge')}
                                                        </span>
                                                    ) : null}
                                                    {isAssigned ? (
                                                        <span className="shrink-0 rounded-full bg-[#EEF2F8] px-2 py-0.5 text-[11px] font-medium text-[#52617F]">
                                                            {t('website.integrationsSettings.assignedToBusinessGroup')}
                                                        </span>
                                                    ) : null}
                                                </span>
                                                {brand.creatorName ? (
                                                    <span className="max-w-full truncate text-xs text-[#8A93B2]">
                                                        {t('website.brandsSettings.simple.createdBy', { name: brand.creatorName })}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                <div role="cell" className="flex items-center px-3">
                                    <ApplicationAccessPermissionControl
                                        value={isSelected ? accessLevel : undefined}
                                        onChange={(nextAccessLevel) => {
                                            if (!canSetPositiveAccess) return;
                                            if (!isSelected) onToggle(brand.id);
                                            onAccessLevelChange(brand.id, nextAccessLevel === 'action' ? 'WRITE' : 'READ');
                                        }}
                                        accessLabels={{
                                            no_access: '',
                                            read: t('website.integrationsSettings.viewOnly'),
                                            action: t('website.integrationsSettings.viewAndTakeAction'),
                                        }}
                                            availableAccessLevels={canSetPositiveAccess
                                                ? ['read', 'action']
                                                : isSelected && accessLevel !== 'no_access' ? [accessLevel] : ['no_access']}
                                        // A required Business group cannot be removed, but its
                                        // delegation ceiling remains the connector's choice.
                                        disabled={disabled || !canSetPositiveAccess}
                                        ariaLabel={`${getBusinessGroupDisplayName(brand)} ${t('website.integrationsSettings.businessGroupAccessCeiling')}`}
                                        canManageAccess={false}
                                        onCanManageAccessChange={() => undefined}
                                        manageAccessLabel=""
                                        manageAccessHelp=""
                                        showManageAccessControl={false}
                                        density="compact"
                                        className="w-[236px] shrink-0"
                                        accessControlClassName="w-[236px] grid-cols-[72px_152px]"
                                    />
                                </div>
                            </div>
                        );
                    })}
                    {brands.length === 0 ? (
                        <div role="row" className="grid w-full grid-cols-[minmax(0,1fr)_260px]">
                            <div role="cell" className="col-span-2 px-4 py-8 text-center text-sm text-[#8A93B2]">
                                {t('website.integrationsSettings.noBusinessGroupsAvailable')}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default function BusinessGroupAssignmentModal({
    isOpen,
    account,
    brands,
    selectedBrandIds,
    assignedBrandIds,
    accessLevels,
    onClose,
    onToggle,
    onAccessLevelChange,
    onSave,
    isSaving = false,
    isSaveDisabled = false,
    secondaryActionLabel,
    description,
    canSetPositiveAccess = true,
    currentAccessLevels,
    requiredBrandIds,
}: BusinessGroupAssignmentModalProps) {
    const { t } = useTranslation();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('website.integrationsSettings.assignBusinessGroupsForAccount', { account })}
            width="max-w-4xl"
            bodyClassName="px-6 pb-6 pt-1"
        >
            <div className="space-y-5">
                <p className="text-sm leading-6 text-[#666F8D]">
                    {description || t('website.integrationsSettings.businessGroupAssignmentDescription')}
                </p>
                <BusinessGroupSelectionList
                    brands={brands}
                    selectedBrandIds={selectedBrandIds}
                    assignedBrandIds={assignedBrandIds}
                    accessLevels={accessLevels}
                    onToggle={onToggle}
                    onAccessLevelChange={onAccessLevelChange}
                    disabled={isSaving}
                    canSetPositiveAccess={canSetPositiveAccess}
                    currentAccessLevels={currentAccessLevels}
                    requiredBrandIds={requiredBrandIds}
                />
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {secondaryActionLabel || t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={isSaving || isSaveDisabled}
                        className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white ${
                            isSaving
                                ? 'cursor-wait bg-[#6D6A79] shadow-none'
                                : 'bg-[#1A162F] transition-colors hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-50'
                        }`}
                    >
                        {isSaving ? (
                            <CircleNotch size={16} weight="bold" className="animate-spin" />
                        ) : t('website.integrationsSettings.saveBusinessGroupAssignments')}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
